import { ShellSnippet } from '@/shared/components/ShellSnippet'
import { SchemaReference } from '@/features/integrations/components/SchemaReference'
import { s } from '@/features/integrations/guideStyles'

// ── Policy guides (per artefact) ──────────────────────────────────────────────
type PolicyArtefact = 'module' | 'state' | 'provider'

export function PolicyGuide({ artefact }: { artefact: PolicyArtefact }) {
  const POLICY_GUIDE: Record<PolicyArtefact, { title: string; head: React.ReactNode; contract: string; examples: string }> = {
  module: {
    title: 'Module Policies',
    head: <><code style={s.code}>input.module</code>: <code style={s.code}>{'{ namespace, name, provider, version, deprecated }'}</code>. Evaluated when a module version is scanned.</>,
    contract: `package terramantle.policy

deny contains msg if {
  input.scan.findings.critical > 0
  msg := sprintf("Module has %d critical finding(s)", [input.scan.findings.critical])
}`,
    examples: `# Approved providers only
approved := {"aws", "google", "azurerm"}
deny contains msg if {
  not approved[input.module.provider]
  msg := sprintf("Provider '%s' is not approved", [input.module.provider])
}

# Block public S3 buckets by rule id
deny contains msg if {
  f := input.findings[_]
  f.rule_id == "AVD-AWS-0086"
  msg := sprintf("Public S3 bucket: %s", [f.title])
}`,
  },
  state: {
    title: 'State Policies',
    head: <><code style={s.code}>input.state</code>: <code style={s.code}>{'{ workspace, resource_count, internal_resource_count }'}</code>. Evaluated when a state file is scanned; a failing enforcing policy blocks the next apply (LOCK) until attested.</>,
    contract: `package terramantle.policy

deny contains msg if {
  input.scan.findings.critical > 0
  msg := sprintf("State has %d critical finding(s)", [input.scan.findings.critical])
}`,
    examples: `# Block secrets found in state
deny contains msg if {
  f := input.findings[_]
  f.category == "secret"
  msg := sprintf("Secret in state: %s", [f.title])
}

# Block internet-exposed resources
deny contains msg if {
  f := input.findings[_]
  f.category == "exposure"
  msg := sprintf("Internet-exposed resource: %s", [f.title])
}`,
  },
  provider: {
    title: 'Provider Policies',
    head: <><code style={s.code}>input.provider</code>: <code style={s.code}>{'{ malware_detected, sbom, source, scan }'}</code> — where <code style={s.code}>scan</code> is this version's own vulnerability rollup (<code style={s.code}>critical, high, has_unfixable_high</code>) to gate on directly, and <code style={s.code}>source</code> carries supply-chain trust signals (<code style={s.code}>stars, contributors, license, commit_cadence, release_cadence, …</code>). Applies to self-built providers only.</>,
    contract: `package terramantle.policy

deny contains msg if {
  input.provider.malware_detected == true
  msg := "Provider source flagged as containing malware"
}`,
    examples: `# Approved source licences (guard null; a missing signal follows the org gate)
approved := {"MIT", "Apache-2.0", "MPL-2.0"}
deny contains msg if {
  input.provider.source.license != null
  not approved[input.provider.source.license]
  msg := sprintf("Source licence '%s' is not permitted", [input.provider.source.license])
}

# Minimum community trust
deny contains msg if {
  input.provider.source.stars != null
  input.provider.source.stars < 25
  msg := "Provider source repo has weak community signal (<25 stars)"
}`,
  },
  }
  const g = POLICY_GUIDE[artefact]
  return (
    <div>
      <h3 style={s.guideTitle}>{g.title}</h3>
      <p style={s.para}>
        Policies are <a href="https://www.openpolicyagent.org/" target="_blank" rel="noopener noreferrer">OPA</a> / Rego rules,
        authored per-artefact on the Policies page. Every policy is <code style={s.code}>package terramantle.policy</code> and
        defines a <code style={s.code}>deny contains msg</code> set: <strong>zero messages → pass</strong>,{' '}
        <strong>one or more → fail</strong>. <em>Enforcing</em> blocks the artefact; <em>advisory</em> only warns. The{' '}
        <code style={s.code}>?</code> icon in the editor shows this exact input while you author.
      </p>
      <ShellSnippet code={g.contract} language="rego" />

      <h3 style={s.subTitle}>Input reference</h3>
      <p style={s.para}>{g.head}</p>
      <SchemaReference artefact={artefact} />

      <h3 style={s.subTitle}>Examples</h3>
      <ShellSnippet code={g.examples} language="rego" />

      {artefact === 'provider' && (
        <>
          <h3 style={s.subTitle}>Missing trust signals</h3>
          <p style={s.para}>
            When a provider's source repo isn't resolvable, its trust signals are <code style={s.code}>null</code>.
            Organisation settings → <strong>Signal fail gate</strong> decides whether that counts as a failure — reference{' '}
            <code style={s.code}>input.settings.on_missing_signal</code> (<code style={s.code}>"ignore"</code> /{' '}
            <code style={s.code}>"hard_fail"</code>) in policies that should respect it.
          </p>
        </>
      )}

      <h3 style={s.subTitle}>Testing</h3>
      <p style={s.para}>
        Evaluate locally with the OPA CLI against a sample <code style={s.code}>input.json</code> — an empty result means
        it passes. The full machine-readable schema is at <code style={s.code}>GET /api/policy-schema/{artefact}</code>.
      </p>
      <ShellSnippet code={`opa eval --data policy.rego --input input.json 'data.terramantle.policy.deny'`} language="bash" />
    </div>
  )
}

